import { i18n } from "@tellescope/i18n";
import {
  useUsersMap,
  useUsersQuery,
  useTellescopeInfiniteQuery,
  TellescopeModelFromName,
  useTellescopeQuery,
} from "@tellescope/tanstack-query";
import {
  Button,
  cn,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@tellescope/ui";
import { formatName } from "@tellescope/utils";
import { Check, X } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { PersonCell } from "./PersonCell";
import { TextCell } from "../../tables";

type PersonMode = "user" | "enduser";

export interface EditablePersonCellProps {
  mode: PersonMode;
  value?: string; // User ID or Enduser ID
  onChange: (newValue: string | null) => void | Promise<void>;
  disabled?: boolean;
  emptyLabel?: string;
  className?: string;
  getBadgeColors?: (value: string) =>
    | {
        bgColor: string;
        textColor: string;
      }
    | undefined;
  clearable?: boolean;
  avatarOnly?: boolean;
}

export const EditablePersonCell = ({
  mode,
  value,
  onChange,
  disabled = false,
  emptyLabel = i18n.t("Not assigned", { ns: "tickets" }),
  className,
  getBadgeColors,
  clearable,
  avatarOnly,
}: EditablePersonCellProps) => {
  const { t } = useTranslation("tickets");
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch users (for "user" mode)
  const usersById = useUsersMap();
  const { data: users = [], isLoading: isLoadingUsers } = useUsersQuery();

  const selectedEnduserQuery = useTellescopeQuery({
    model: "endusers",
    method: "getOne",
    request: value ?? "",
    enabled: mode === "enduser" && !!value,
  });

  // Fetch endusers (for "enduser" mode)
  const endusersQuery = useTellescopeInfiniteQuery({
    model: "endusers",
    method: "getSome",
    request: {
      limit: 50,
      search: searchQuery ? { query: searchQuery } : undefined,
    },
    select: (data): TellescopeModelFromName<"endusers">[] => {
      const currentEnduser = selectedEnduserQuery.data;
      if (!currentEnduser) return data.pages.flat();
      return [currentEnduser, ...data.pages.flat()];
    },
    enabled: mode === "enduser",
  });

  const endusers = useMemo(() => endusersQuery.data ?? [], [endusersQuery.data]);
  const isLoadingEndusers = endusersQuery.isLoading;

  // Create a map for endusers by ID
  const endusersById = useMemo(() => {
    const selectedEnduser = selectedEnduserQuery.data;
    const map: Record<string, TellescopeModelFromName<"endusers">> = value && mode === "enduser" && selectedEnduser
      ? { [value]: selectedEnduser }
      : {};
    for (const enduser of endusers) {
      map[enduser.id] = enduser;
    }
    return map;
  }, [endusers, mode, selectedEnduserQuery.data, value]);

  // Get the appropriate list and loading state based on mode
  const people = mode === "user" ? users : endusers;
  const isLoading = mode === "user" ? isLoadingUsers : isLoadingEndusers;
  const peopleById = mode === "user" ? usersById : endusersById;

  // Get the selected person
  const selectedPerson = useMemo(() => {
    if (!value) return null;
    return peopleById[value];
  }, [value, peopleById]);

  // Filter people based on search query (only for users, endusers are filtered server-side)
  const filteredPeople = useMemo(() => {
    if (mode === "enduser") {
      // Endusers are already filtered server-side
      return people;
    }

    // Filter users client-side
    if (!searchQuery) return people;

    const query = searchQuery.toLowerCase();
    return people.filter((person) => {
      const fullName = formatName(person).toLowerCase();
      const email = person.email?.toLowerCase() || "";
      return fullName.includes(query) || email.includes(query);
    });
  }, [people, searchQuery, mode]);

  const badgeColors = useMemo(() => {
    const defaultColors = {
      bgColor: "bg-muted" + (disabled ? "" : " hover:bg-gray-300"),
      textColor: "text-muted-foreground" + (disabled ? "" : " hover:text-muted-foreground"),
    };

    if (value === undefined) return defaultColors;

    return getBadgeColors?.(value) ?? defaultColors;
  }, [disabled, getBadgeColors, value]);

  const handleSelect = useCallback(
    (personId: string) => {
      // Toggle selection: if clicking the same person, deselect
      onChange(personId === value ? null : personId);
      setOpen(false);
      setSearchQuery("");
    },
    [onChange, value]
  );

  const person = value ? peopleById[value] : undefined;

  if (!person && !!value) {
    return <TextCell>{mode === "user" ? t("Unknown User") : t("Unknown Patient")}</TextCell>;
  }

  if (disabled && person) {
    return (
      <PersonCell
        person={person}
        className={cn(badgeColors.bgColor, badgeColors.textColor, className)}
        avatarOnly={avatarOnly}
      />
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          className={cn(
            "h-auto justify-start font-medium px-2 py-1 text-xs group",
            !avatarOnly && badgeColors.bgColor,
            !avatarOnly && badgeColors.textColor,
            avatarOnly && "p-0",
            !selectedPerson && "text-muted-foreground",
            className
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {person ? <PersonCell person={person} avatarOnly={avatarOnly} /> : emptyLabel}
          {!!clearable && !!value && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onChange("");
              }}
              variant="ghost"
              className="opacity-0 group-hover:opacity-100 hover:bg-white"
              size="sm-icon"
            >
              <X />
            </Button>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[300px] p-0" sideOffset={10}>
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={mode === "user" ? t("Search users...") : t("Search contacts...")}
            value={searchQuery}
            onValueChange={setSearchQuery}
          />
          <CommandList>
            <CommandEmpty>
              {isLoading ? t("Loading...") : mode === "user" ? t("No users found") : t("No contacts found")}
            </CommandEmpty>
            <CommandGroup>
              {filteredPeople.map((person) => {
                const isSelected = value === person.id;
                const name = formatName(person);
                const displayName = name || t("Unknown");

                return (
                  <CommandItem key={person.id} value={person.id} onSelect={handleSelect} className="cursor-pointer">
                    <div className="flex items-center gap-2 w-full">
                      <Check className={cn("h-4 w-4 shrink-0", isSelected ? "opacity-100" : "opacity-0")} />
                      <div className="flex flex-col overflow-hidden flex-1">
                        <span className="text-sm truncate">{displayName}</span>
                        {person.email && <span className="text-xs text-muted-foreground truncate">{person.email}</span>}
                      </div>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
